import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type {
  SkillAuditAction,
  SkillsAuditLabels,
  SkillsDetailLabels,
  SkillsEditorLabels,
  SkillsListLabels,
  SkillsVersionLabels,
} from '@terra/ui-skills';

export function useSkillsListLabels(): SkillsListLabels {
  const t = useTranslations('customSkills');

  return useMemo(
    () => ({
      title: t('title'),
      description: t('description'),
      newSkill: t('newSkill'),
      noResults: t('noResults'),
      loading: t('loading'),
      allSources: t('allSources'),
      allStatuses: t('allStatuses'),
      searchPlaceholder: t('searchPlaceholder'),
      platform: t('platform'),
      customer: t('customer'),
      draft: t('draft'),
      published: t('published'),
      tombstoned: t('tombstoned'),
      emptyTitle: t('emptyTitle'),
      emptyBody: t('emptyBody'),
      emptyCta: t('emptyCta'),
      tenantScopeNote: t('tenantScopeNote'),
      lastUpdatedLabel: (date: string) => t('lastUpdated', { date }),
      publishedAtLabel: (date: string) => t('publishedOn', { date }),
      draftBadgeLabel: t('draftBadge'),
    }),
    [t],
  );
}

export function useSkillsEditorLabels(): SkillsEditorLabels {
  const t = useTranslations('customSkills');
  const tEditor = useTranslations('customSkills.editor');
  const tCommon = useTranslations('common');

  return useMemo(
    () => ({
      createTitle: tEditor('createTitle'),
      createSubtitle: tEditor('createSubtitle'),
      editTitle: tEditor('editTitle'),
      editSubtitle: tEditor('editSubtitle'),
      identitySectionTitle: tEditor('identitySectionTitle'),
      identitySectionHelp: tEditor('identitySectionHelp'),
      instructionsSectionTitle: tEditor('instructionsSectionTitle'),
      instructionsSectionHelp: tEditor('instructionsSectionHelp'),
      activationSectionTitle: tEditor('activationSectionTitle'),
      activationSectionHelp: tEditor('activationSectionHelp'),
      slug: {
        title: tEditor('slug.title'),
        help: tEditor('slug.help'),
        errorPattern: tEditor('slug.errorPattern'),
        errorLength: tEditor('slug.errorLength'),
      },
      name: {
        title: tEditor('name.title'),
        help: tEditor('name.help'),
        errorRequired: tEditor('name.errorRequired'),
        errorTooLong: (max: number) => tEditor('name.errorTooLong', { max }),
      },
      summary: {
        title: tEditor('summary.title'),
        help: tEditor('summary.help'),
        errorRequired: tEditor('summary.errorRequired'),
        errorTooLong: (max: number) => tEditor('summary.errorTooLong', { max }),
        charCount: (count: number, max: number) =>
          tEditor('summary.charCount', { count, max }),
      },
      body: {
        title: tEditor('body.title'),
        help: tEditor('body.help'),
        errorRequired: tEditor('body.errorRequired'),
        errorTooLong: tEditor('body.errorTooLong'),
        byteCount: (bytes: number, max: number) =>
          tEditor('body.byteCount', { bytes, max }),
      },
      writeTab: tEditor('writeTab'),
      splitTab: tEditor('splitTab'),
      previewTab: tEditor('previewTab'),
      previewEmpty: tEditor('previewEmpty'),
      emptyField: tEditor('emptyField'),
      refineField: tEditor('refineField'),
      aiOnlyEditNote: tEditor('aiOnlyEditNote'),
      safetyReviewHelp: tEditor('safetyReviewHelp'),
      saveDraft: tCommon('save'),
      publish: tCommon('publish'),
      cancel: tEditor('cancel'),
      close: tEditor('close'),
      saved: t('saved'),
      publishedToast: t('publishedToast'),
      rolledBackToast: t('rolledBackToast'),
      deletedToast: t('deletedToast'),
      restoredToast: t('restoredToast'),
      delete: tEditor('delete'),
      edit: tEditor('edit'),
      restore: tEditor('restore'),
      error: t('saveError'),
      successTitle: t('successTitle'),
      errorTitle: t('errorTitle'),
      infoTitle: t('infoTitle'),
      unsavedChangesNote: tEditor('unsavedChangesNote'),
      deleteConfirmTitle: tEditor('deleteConfirmTitle'),
      deleteConfirmBody: tEditor('deleteConfirmBody'),
      deleteConfirm: tEditor('deleteConfirm'),
      aiCreate: tEditor('aiCreate'),
      aiCreateHelp: tEditor('aiCreateHelp'),
      aiCreateRejectedTitle: tEditor('aiCreateRejectedTitle'),
      refineFieldChatRequest: tEditor('refineFieldChatRequest'),
      fieldContextSelectionLabel: tEditor('fieldContextSelectionLabel'),
      fieldContextFieldLabel: tEditor('fieldContextFieldLabel'),
      fieldContextSelectedText: tEditor('fieldContextSelectedText'),
      fieldContextEntireField: tEditor('fieldContextEntireField'),
      fieldContextRawTitle: tEditor('fieldContextRawTitle'),
      fieldContextRawUntrustedDataNotice: tEditor(
        'fieldContextRawUntrustedDataNotice',
      ),
      safetyReviewChatTitle: tEditor('safetyReviewChatTitle'),
      safetyReviewChatIssue: ({
        category,
        severity,
        field,
        message,
      }: {
        category: string;
        severity: string;
        field: string;
        message: string;
      }) =>
        tEditor('safetyReviewChatIssue', {
          category,
          severity,
          field,
          message,
        }),
      safetyReviewChatExcerpt: (excerpt: string) =>
        tEditor('safetyReviewChatExcerpt', { excerpt }),
      safetyReviewChatDetail: (detail: string) =>
        tEditor('safetyReviewChatDetail', { detail }),
      safetyReviewChatRequest: tEditor('safetyReviewChatRequest'),
    }),
    [t, tCommon, tEditor],
  );
}

export function useSkillsVersionLabels(): SkillsVersionLabels {
  const tVersions = useTranslations('customSkills.versions');

  return useMemo(
    () => ({
      title: tVersions('title'),
      versionLabel: (version: number) => tVersions('version', { version }),
      publishedAtLabel: (date: string) => tVersions('publishedAt', { date }),
      draft: tVersions('draft'),
      rollbackTo: tVersions('rollbackTo'),
      current: tVersions('current'),
      noHistory: tVersions('noHistory'),
    }),
    [tVersions],
  );
}

export function useSkillsAuditLabels(): SkillsAuditLabels {
  const tAudit = useTranslations('customSkills.audit');

  return useMemo(
    () => ({
      title: tAudit('title'),
      byPlatform: tAudit('byPlatform'),
      byUserLabel: (actorId: string) => tAudit('byUser', { actorId }),
      noActivity: tAudit('noActivity'),
      actionLabel: (action: SkillAuditAction | string) => {
        switch (action) {
          case 'CREATE':
            return tAudit('actionCreate');
          case 'UPDATE_DRAFT':
            return tAudit('actionUpdateDraft');
          case 'PUBLISH':
            return tAudit('actionPublish');
          case 'ROLLBACK':
            return tAudit('actionRollback');
          case 'DELETE':
            return tAudit('actionDelete');
          case 'RESTORE':
            return tAudit('actionRestore');
          case 'PLATFORM_SYNC':
            return tAudit('actionPlatformSync');
          default:
            return action;
        }
      },
    }),
    [tAudit],
  );
}

export function useSkillsDetailLabels(): SkillsDetailLabels {
  const tDetail = useTranslations('customSkills.detail');

  return useMemo(
    () => ({
      identityTitle: tDetail('identityTitle'),
      slugLabel: tDetail('slugLabel'),
      statusLabel: tDetail('statusLabel'),
      createdLabel: tDetail('createdLabel'),
      updatedLabel: tDetail('updatedLabel'),
      untitledSkill: tDetail('untitledSkill'),
      triggerTitle: tDetail('triggerTitle'),
      triggerEmpty: tDetail('triggerEmpty'),
      instructionsTitle: tDetail('instructionsTitle'),
      instructionsEmpty: tDetail('instructionsEmpty'),
      tenantScopeNote: tDetail('tenantScopeNote'),
    }),
    [tDetail],
  );
}
